import * as React from 'react';
import styles from './index.less';
import FDrawer from '@/components/FDrawer';
import { Radio, Space } from 'antd';
import FComponentsLib from '@freelog/components-lib';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import FResourceTypeFilter3 from '@/components/FResourceTypeFilter3';
import FCoverImage from '@/components/FCoverImage';
import * as AHooks from 'ahooks';
import FResourceStatusBadge from '@/components/FResourceStatusBadge';
import dayjs from 'dayjs';
import { listStateAndListMore } from '@/components/FListFooter';
import FLoadingTip from '@/components/FLoadingTip';
import FNoDataTip from '@/components/FNoDataTip';
import fPromiseModalConfirm from '@/components/fPromiseModalConfirm';
import FInput_Search from '@/components/FInput_Search';

interface FCollectionSelectorDrawerProps {
  onConfirm?(collectionID: string): void;

  onClosed?(): void;
}

function FCollectionSelectorDrawer({ onConfirm, onClosed }: FCollectionSelectorDrawerProps) {
  const [$open, set$open, get$open] = FUtil.Hook.useGetState<boolean>(true);

  const [$resourceTypeCodes, set$resourceTypeCodes, get$resourceTypeCodes] =
    FUtil.Hook.useGetState<{
      value: string;
      label: string;
      values: string[];
      labels: string[];
    }>({
      value: '#all',
      // label: '全部',
      label: FI18n.i18nNext.tAuto('resourcetype_filter_collection_all'),
      values: ['#all'],
      // labels: ['全部'],
      labels: [FI18n.i18nNext.tAuto('resourcetype_filter_collection_all')],
    });
  const [$searchInput, set$searchInput, get$searchInput] = FUtil.Hook.useGetState<string>('');
  const [$collectionList, set$collectionList, get$collectionList] = FUtil.Hook.useGetState<
    {
      id: string;
      cover: string;
      name: string;
      title: string;
      type: string[];
      status: 'freeze' | 'online' | 'unreleased' | 'offline';
      updateDate: string;
      collectionStatus: 'finish' | 'unFinish';
    }[]
  >([]);
  const [$listState, set$listState, get$listState] = FUtil.Hook.useGetState<
    'loading' | 'noData' | 'noSearchResult' | 'loaded'
  >('loading');
  const [$listMore, set$listMore, get$listMore] = FUtil.Hook.useGetState<
    'loading' | 'andMore' | 'noMore'
  >('loading');
  const [$checkedCollectionID, set$checkedCollectionID, get$checkedCollectionID] =
    FUtil.Hook.useGetState<string>('');

  const { run: run_search } = AHooks.useDebounceFn(
    () => {
      fetchCollections(true);
    },
    {
      wait: 300,
    },
  );

  async function fetchCollections(restart: boolean = false) {
    if (restart) {
      set$listState('loading');
    }
    set$listMore('loading');
    const params: Parameters<typeof FServiceAPI.Resource.list>[0] = {
      // skip: resource_List.length,
      skip: 0,
      limit: FUtil.Predefined.pageSize,
      keywords: get$searchInput() || undefined,
      resourceTypeCode:
        get$resourceTypeCodes().value === '#all' ? undefined : get$resourceTypeCodes().value,
      isSelf: 1,
      subjectType: 4,
    };

    const {
      ret,
      errCode,
      data: data_collection,
    }: {
      ret: number;
      errCode: number;
      data: {
        limit: number;
        skip: number;
        totalItem: number;
        dataList: {
          resourceId: string;
          resourceName: string;
          resourceTitle: string;
          resourceType: string[];
          latestVersion: string;
          coverImages: string[];
          status: 0 | 1 | 2 | 4;
          policies: {
            policyId: string;
            policyName: string;
            status: 0 | 1;
          }[];
          userId: number;
          updateDate: string;
          username: string;
          operationType: 0 | 1 | 2 | 3 | 4 | 5;
          serializeStatus?: 0 | 1;
        }[];
      };
    } = await FServiceAPI.Resource.list(params);

    set$collectionList(
      data_collection.dataList.map((c) => {
        return {
          id: c.resourceId,
          cover: c.coverImages[0] || '',
          name: c.resourceName,
          title: c.resourceTitle,
          type: c.resourceType,
          status:
            c.status === 2
              ? 'freeze'
              : c.status === 1
              ? 'online'
              : c.status === 0
              ? 'unreleased'
              : 'offline',
          updateDate: dayjs(c.updateDate).format(FUtil.Predefined.momentDateFormat),
          collectionStatus: c.serializeStatus === 1 ? 'finish' : 'unFinish',
        };
      }),
    );

    const { state, more } = listStateAndListMore({
      list_Length: get$collectionList().length,
      total_Length: data_collection.totalItem,
      has_FilterCriteria: get$searchInput() !== '' || get$resourceTypeCodes().value !== '#all',
    });

    set$listState(state);
    set$listMore(more);
  }

  return (
    <FDrawer
      open={$open}
      width={700}
      // title={'选择合集'}
      title={FI18n.i18nNext.tAuto('addtomycollection_title')}
      topRight={
        <Space size={30}>
          <FComponentsLib.FTextBtn
            type='default'
            onClick={() => {
              set$open(false);
            }}
          >
            {/* 取消 */}
            {FI18n.i18nNext.tAuto('btn_cancel')}
          </FComponentsLib.FTextBtn>

          <FComponentsLib.FRectBtn
            type='primary'
            disabled={$checkedCollectionID === ''}
            onClick={() => {
              onConfirm?.(get$checkedCollectionID());
              set$open(false);
            }}
          >
            {/* 加入合集 */}
            {FI18n.i18nNext.tAuto('addtomycollection_btn_submit')}
          </FComponentsLib.FRectBtn>
        </Space>
      }
      afterOpenChange={(o) => {
        if (o) {
          fetchCollections();
        } else {
          onClosed?.();
        }
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 30 }}>
          <FResourceTypeFilter3
            value={$resourceTypeCodes}
            onChange={(value) => {
              set$resourceTypeCodes(value);
              run_search();
            }}
            subjectTypeArr={['collection']}
          />

          <FComponentsLib.FTextBtn
            type={'primary'}
            onClick={async () => {
              self.open(FUtil.LinkTo.collectionCreator());
              const result: boolean = await fPromiseModalConfirm({
                title: '提示',
                description: '已完成合集的创建',
                // cancelText: '否',
                cancelText: FI18n.i18nNext.t(
                  'cqr_itemmgmt_additem_newresource_confirmation_btn_no',
                ),
                // okText: '是',
                okText: FI18n.i18nNext.t('cqr_itemmgmt_additem_newresource_confirmation_btn_yes'),
              });
              if (result) {
                fetchCollections(true);
              }
            }}
          >
            {/* 创建新合集 */}
            {FI18n.i18nNext.tAuto('addtomycollection_btn_createcollection')}
          </FComponentsLib.FTextBtn>
        </div>

        {/* <FComponentsLib.FInput.FSearch */}
        <FInput_Search
          value={$searchInput}
          style={{ width: 200 }}
          placeholder={FI18n.i18nNext.tAuto('my_collections_search_hint')}
          onChange={(e) => {
            set$searchInput(e.target.value);
            run_search();
          }}
        />
      </div>
      <div style={{ height: 30 }} />
      {$listState === 'loading' && <FLoadingTip height={'calc(100vh - 300px)'} />}

      {$listState === 'noData' && (
        <FNoDataTip height={'calc(100vh - 300px)'} tipText={'暂无数据'} />
      )}

      {$listState === 'noSearchResult' && (
        <FNoDataTip height={'calc(100vh - 300px)'} tipText={'无搜索数据'} />
      )}

      {$listState === 'loaded' && (
        <div className={styles.collections}>
          {$collectionList.map((c) => {
            return (
              <div
                className={styles.collection}
                style={
                  c.status === 'freeze'
                    ? {
                        cursor: 'not-allowed',
                        opacity: 0.4,
                      }
                    : {}
                }
                key={c.id}
                onClick={() => {
                  if (c.status === 'freeze') {
                    return;
                  }
                  set$checkedCollectionID(c.id);
                }}
              >
                <Radio checked={$checkedCollectionID === c.id} />
                <div className={styles.card}>
                  <div className={styles.cover}>
                    <FCoverImage src={c.cover} width={100} style={{ borderRadius: 4 }} />
                    <div style={{ position: 'absolute', top: 5, left: 5, width: '100%' }}>
                      <FResourceStatusBadge status={c.status} />
                    </div>
                  </div>
                  <div className={styles.content}>
                    <FComponentsLib.FContentText
                      text={c.title}
                      type={'highlight'}
                      singleRow={true}
                      style={{ maxWidth: 460 }}
                    />
                    <div style={{ height: 10 }} />
                    <FComponentsLib.FContentText text={c.type.join('/')} type={'additional2'} />
                    <div style={{ height: 10 }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <FComponentsLib.FContentText
                        type={'additional2'}
                        text={
                          c.collectionStatus === 'finish'
                            ? // '已完结'
                              FI18n.i18nNext.tAuto('cqr_itemmgmt_updatedstates_completed')
                            : // '连载中'
                              FI18n.i18nNext.tAuto('cqr_itemmgmt_updatestates_ongoing')
                        }
                        style={{
                          color: c.collectionStatus === 'finish' ? '#E9A923' : '#42C28C',
                        }}
                      />
                      <FComponentsLib.FContentText
                        type={'additional2'}
                        text={'|'}
                        style={{ color: '#666' }}
                      />
                      <FComponentsLib.FContentText
                        type={'additional2'}
                        // text={`最近更新${c.updateDate}`}
                        text={FI18n.i18nNext.tAuto('resource_card_label_lastupdated', {
                          LatestUpdatedTime: c.updateDate,
                        })}
                        style={{ color: '#666' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </FDrawer>
  );
}

export default FCollectionSelectorDrawer;
